/*
 * DREPEEZ wishlist. Saved in this browser's localStorage (no account or app needed), so it lasts
 * across refreshes and visits on the same browser and device, but doesn't follow the customer to
 * another device. Every heart for a product stays in sync, including across open tabs.
 */
(() => {
  const KEY = 'drepeez_wishlist_v1';
  const MAX = 60;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Storage ---------- */

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((item) => item && typeof item.h === 'string' && item.h) : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {}
    sync();
  };

  const has = (handle) => read().some((item) => item.h === handle);

  const add = (handle) => {
    const list = read().filter((item) => item.h !== handle);
    list.unshift({ h: handle, t: Date.now() });
    write(list);
  };

  const remove = (handle) => write(read().filter((item) => item.h !== handle));

  /* ---------- UI sync ---------- */

  const sync = () => {
    const saved = new Set(read().map((item) => item.h));
    document.querySelectorAll('[data-bf-wl]').forEach((button) => {
      const on = saved.has(button.dataset.bfWl);
      if (button.getAttribute('aria-pressed') !== String(on)) button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('is-saved', on);
      const title = button.dataset.title || 'this product';
      button.setAttribute('aria-label', on ? `Remove ${title} from wishlist` : `Save ${title} to wishlist`);
      const label = button.querySelector('[data-bf-wl-label]');
      if (label) label.textContent = on ? 'Saved to wishlist' : 'Add to wishlist';
    });
    document.querySelectorAll('[data-bf-wl-count]').forEach((count) => {
      count.textContent = saved.size > 99 ? '99+' : String(saved.size);
      count.hidden = saved.size === 0;
    });
    document.querySelectorAll('[data-bf-wl-header]').forEach((link) => {
      link.setAttribute('aria-label', saved.size ? `Wishlist, ${saved.size} saved` : 'Wishlist');
    });
  };

  /* ---------- Toast ---------- */

  let toast;
  let toastTimer;
  const showToast = (text) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bf-wl-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.3s-7.4-4.5-9.2-9C1.6 8 3.4 4.6 6.9 4.6c2 0 3.6 1.1 5.1 3 1.5-1.9 3.1-3 5.1-3 3.5 0 5.3 3.4 4.1 6.7-1.8 4.5-9.2 9-9.2 9z"/></svg><span></span>';
      document.body.appendChild(toast);
    }
    toast.querySelector('span').textContent = text;
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1900);
  };

  /* ---------- Clicks ---------- */

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-bf-wl-remove]');
    if (removeButton) {
      event.preventDefault();
      remove(removeButton.dataset.bfWlRemove);
      showToast('Removed from wishlist');
      return;
    }
    const button = event.target.closest('[data-bf-wl]');
    if (!button || !button.dataset.bfWl) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = button.dataset.bfWl;
    if (has(handle)) {
      remove(handle);
      showToast('Removed from wishlist');
    } else {
      add(handle);
      showToast('Added to wishlist');
      if (!reduceMotion.matches) {
        button.classList.remove('is-popping');
        void button.offsetWidth;
        button.classList.add('is-popping');
      }
    }
  });

  // Other tabs, and hearts that appear later (cart drawer, quick add sheet, wishlist page).
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) sync();
  });
  document.addEventListener('bf:wishlist-sync', sync);
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  }).observe(document.body, { childList: true, subtree: true });

  /* ---------- Wishlist page ---------- */

  const page = document.querySelector('[data-bf-wl-page]');

  const setCount = (n) => {
    const count = page.querySelector('[data-bf-wl-page-count]');
    count.textContent = n ? `${n} saved piece${n === 1 ? '' : 's'}` : '';
    page.querySelector('[data-bf-wl-empty]').hidden = n > 0;
  };

  const loadPage = async () => {
    const grid = page.querySelector('[data-bf-wl-grid]');
    const skeleton = page.querySelector('[data-bf-wl-skeleton]');
    const list = read();
    if (!list.length) {
      setCount(0);
      return;
    }
    skeleton.hidden = false;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const results = await Promise.all(
      list.map((item) =>
        fetch(`${root}products/${encodeURIComponent(item.h)}?section_id=bf-wishlist-card`)
          .then((res) => {
            if (res.status === 404) return { handle: item.h, gone: true };
            return res.ok ? res.text().then((html) => ({ handle: item.h, html })) : { handle: item.h };
          })
          .catch(() => ({ handle: item.h }))
      )
    );
    // Products that no longer exist are dropped from the list.
    const gone = results.filter((r) => r.gone).map((r) => r.handle);
    if (gone.length) write(read().filter((item) => !gone.includes(item.h)));

    const parser = new DOMParser();
    results.forEach((result, i) => {
      if (!result.html) return;
      const item = parser.parseFromString(result.html, 'text/html').querySelector('.bf-wl-item');
      if (!item) return;
      item.style.setProperty('--i', String(i));
      grid.appendChild(document.importNode(item, true));
    });
    skeleton.hidden = true;
    setCount(grid.children.length);
    sync();
  };

  if (page) {
    // Removing a heart on this page takes the card out of the grid.
    const grid = page.querySelector('[data-bf-wl-grid]');
    const prune = () => {
      const saved = new Set(read().map((item) => item.h));
      grid.querySelectorAll('[data-bf-wl-item]').forEach((item) => {
        if (saved.has(item.dataset.bfWlItem) || item.classList.contains('is-removing')) return;
        item.classList.add('is-removing');
        const done = () => {
          item.remove();
          setCount(grid.children.length);
        };
        reduceMotion.matches ? done() : setTimeout(done, 450);
      });
    };
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-bf-wl], [data-bf-wl-remove]')) setTimeout(prune, 0);
    });
    window.addEventListener('storage', (event) => {
      if (event.key === KEY) prune();
    });
    loadPage();
  }

  sync();
})();
